import React, { Fragment, useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Button, Card, CardBody, CardFooter, CardHeader, Col, FormGroup, Input, Label, Row } from "reactstrap";
import { useService } from "../../context/ServiceContext";
import LoadedContent from "../LoadedContent";
import { fireAlert } from "../../utils/alerts";
import ErrorAlertContent from "../ErrorAlertContent";

function CertificateThirdPartySeriesProductSearch({ search, keyCompany, existingCertificate, selectedSeries, setSelectedSeries, selectedProducts, setSelectedProducts }) {
  const intl = useIntl();

  const { certificateSeriesProductsProvider } = useService();

  const [hasSearched, setHasSearched] = useState(false);

  const [customSeriesName, setCustomSeriesName] = useState(null);
  const [seriesData, setSeriesData] = useState();
  const [productData, setProductData] = useState();

  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!search) return;

    setSelectedSeries([]);
    setSelectedProducts([]);

    loadData();
  }, [search, keyCompany]);

  async function onSeriesClick(serie) {
    let series = [...selectedSeries];
    if (series.some((s) => s.codSerieComercial == serie.codSerieComercial)) series = series.filter((s) => s.codSerieComercial != serie.codSerieComercial);
    else series = series.concat(serie);

    setSelectedSeries(series);
  }

  async function onProductClick(product) {
    let products = [...selectedProducts];
    if (products.some((p) => p.codProductoComercial == product.codProductoComercial)) products = products.filter((p) => p.codProductoComercial != product.codProductoComercial);
    else products = products.concat(product);

    setSelectedProducts(products);
  }

  async function loadData() {
    try {
      const seriesProducts = await certificateSeriesProductsProvider.get({ keyCompany, search });

      const distinctPairs = [
        ...new Set(
          seriesProducts.map((item) =>
            JSON.stringify({
              shortNameComercial: item.shortNameComercial,
              codSerieComercial: item.codSerieComercial,
            }),
          ),
        ),
      ].map((str) => JSON.parse(str));

      setHasSearched(true);
      setSeriesData(distinctPairs);
      setProductData(seriesProducts);
    } catch (error) {
      setSeriesData(null);
      setProductData(null);

      fireAlert(
        <ErrorAlertContent message={intl.formatMessage(error.localization)} traceId={error.traceId} />,
        intl.formatMessage({ id: "app.error", defaultMessage: "Error" }),
        "error",
      );
    }
  }

  function selectAll() {
    const products = [...selectedProducts];

    productData
      .filter((p) => selectedSeries?.some((s) => s.codSerieComercial == p.codSerieComercial))
      .forEach((product) => {
        if (products.some((p) => p.codProductoComercial == product.codProductoComercial) == false) {
          products.push(product);
        }
      });

    setSelectedProducts(products);
  }

  function deselectAll() {
    const products = [...selectedProducts];
    for (let i = 0; i < productData.length; i++) {
      const product = productData[i];
      if (products.some((p) => p.codProductoComercial == product.codProductoComercial)) {
        products.push(product);
      }
    }
    const sp = productData.map((p) => p.codProductoComercial);
    setSelectedProducts(products.filter((p) => !sp.includes(p.codProductoComercial)));
  }

  async function customSeries() {
    const { value: series } = await fireAlert(
      intl.formatMessage({ id: "certificates.enter_name_of_new_type", defaultMessage: "Please enter the name of the new type" }),
      intl.formatMessage({ id: "certificates.create_a_type", defaultMessage: "Create a type" }),
      null,
      { input: "text" },
    );
    if (series != null && series.trim() != "") {
      setCustomSeriesName(series);
    } else {
      setCustomSeriesName(null);
    }
  }

  return (
    <LoadedContent loading={isLoading} overlay={true}>
      {hasSearched && productData?.length == 0 && customSeriesName == null && (
        <Card>
          <CardBody>
            <FormattedMessage id="certificates.no_products_found" defaultMessage="No products found that match that search. Would you like to create a type?" />
          </CardBody>
          <CardFooter>
            <button type="button" className="btn btn-outline-danger" onClick={customSeries}>
              <FormattedMessage id="certificates.create_a_type" defaultMessage="Create a type" />
            </button>
          </CardFooter>
        </Card>
      )}

      {(seriesData?.length > 0 || productData?.length > 0) && (
        <Card>
          <CardHeader>
            <FormattedMessage id="certificates.product_selection" defaultMessage="Product selection" />
          </CardHeader>
          <CardBody>
            <Row>
              <Col sm={6}>
                <h3>
                  <FormattedMessage id="app.series" defaultMessage="Series" />
                </h3>
                <div style={{ maxHeight: 300, overflowY: "scroll" }}>
                  {seriesData
                    ?.filter((s) => s.shortNameComercial)
                    .map((s) => (
                      <div key={s.codSerieComercial} className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={selectedSeries.some((s1) => s1.codSerieComercial == s.codSerieComercial)}
                          id={`check-series-${s.codSerieComercial}`}
                          onClick={() => onSeriesClick(s)}
                        />
                        <label className="form-check-label" for={`check-series-${s.codSerieComercial}`}>
                          {s.shortNameComercial}
                        </label>
                      </div>
                    ))}
                </div>
              </Col>
              <Col sm={6}>
                {productData && (
                  <>
                    <h3 style={{ display: "inline-block" }}>
                      <FormattedMessage id="certificates.products" defaultMessage="Products" />
                    </h3>
                    <button className="btn btn-link btn-sm" onClick={selectAll}>
                      <FormattedMessage id="app.select_all" defaultMessage="Select all" />
                    </button>
                    <button className="btn btn-link btn-sm" onClick={deselectAll}>
                      <FormattedMessage id="app.deselect_all" defaultMessage="Deselect all" />
                    </button>
                    <div style={{ maxHeight: 300, overflowY: "scroll" }}>
                      {productData
                        ?.filter((p) => p.productoComercial && selectedSeries?.some((s) => s.codSerieComercial == p.codSerieComercial))
                        .map((p) => (
                          <div key={p.codProductoComercial} className="form-check">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              checked={selectedProducts.some((p1) => p1.codProductoComercial == p.codProductoComercial)}
                              id={`check-product-${p.codProductoComercial}`}
                              onClick={() => onProductClick(p)}
                            />
                            <label className="form-check-label" for={`check-product-${p.codProductoComercial}`}>
                              {p.productoComercial}
                            </label>
                          </div>
                        ))}
                    </div>
                  </>
                )}
              </Col>
            </Row>
          </CardBody>
        </Card>
      )}
      {(selectedSeries?.length > 0 || selectedProducts?.length > 0 || customSeriesName != null || existingCertificate) && (
        <>
          <Row>
            {customSeriesName != null && (
              <Col sm={6}>
                <Card className="mt-3">
                  <CardHeader>Creating a new type</CardHeader>
                  <CardBody>
                    {customSeriesName}{" "}
                    <Button type="button" className="btn btn-link text-dark ml-3" onClick={() => setCustomSeriesName(null)}>
                      <i className="fas fa-trash-alt"></i>
                    </Button>
                  </CardBody>
                </Card>
              </Col>
            )}

            {customSeriesName == null && existingCertificate == null && (
              <Col sm="6">
                <Card className="mt-3">
                  <CardHeader className="d-flex align-items-center">
                    <div className="flex-grow-1">
                      <FormattedMessage id="certificates.selected_products" defaultMessage="Selected Products" />
                    </div>
                    <button
                      type="button"
                      className="btn btn-link btn-sm"
                      onClick={() => {
                        setSelectedSeries([]);
                        setSelectedProducts([]);
                      }}
                    >
                      <FormattedMessage id="app.remove_all" defaultMessage="Remove all" />
                    </button>
                  </CardHeader>
                  <CardBody>
                    <Row>
                      <Col>
                        {selectedSeries
                          .filter((s) => selectedProducts.some((p) => p.codSerieComercial == s.codSerieComercial) == false)
                          .map((p) => (
                            <Fragment key={p.id}>
                              <span className="badge badge-pill badge-primary">
                                {p.shortNameComercial} <i className="fas fa-times" style={{ cursor: "pointer" }} onClick={() => onSeriesClick(p)}></i>
                              </span>{" "}
                            </Fragment>
                          ))}
                        {selectedProducts.map((p) => (
                          <Fragment key={p.id}>
                            <span className="badge badge-pill badge-primary">
                              {p.productoComercial} <i className="fas fa-times" style={{ cursor: "pointer" }} onClick={() => onProductClick(p)}></i>
                            </span>{" "}
                          </Fragment>
                        ))}
                      </Col>
                    </Row>
                  </CardBody>
                </Card>
              </Col>
            )}
          </Row>

          {/* <Row>
            <Col>
              {existingCertificate && (
                <button type="button" className="btn btn-outline-danger" onClick={update}>
                  <FormattedMessage id="app.update" defaultMessage="Update" />
                </button>
              )}
              {!existingCertificate && (
                <>
                  <button type="button" className="btn btn-outline-danger" onClick={save}>
                    <FormattedMessage id="app.save" defaultMessage="Save" />
                  </button>
                  <button type="button" className="btn btn-outline-danger" onClick={preview}>
                    <FormattedMessage id="app.preview" defaultMessage="Preview" />
                  </button>
                </>
              )}
            </Col>
          </Row> */}
        </>
      )}
    </LoadedContent>
  );
}

export default CertificateThirdPartySeriesProductSearch;
